import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/soul";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { PenLine, Check } from "lucide-react";

export function VerseReflection({ verse, source = "red-letters", savedNote, onSaved }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState(savedNote || "");
  const [shared, setShared] = useState(false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!note.trim()) {
      toast.error("Write what you received first.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/reflections", {
        verse_ref: verse.reference,
        verse_text: verse.text,
        source,
        note,
        shared,
      });
      toast.success(shared ? "Shared with the community." : "Your reflection has been kept.");
      setOpen(false);
      onSaved && onSaved(verse.reference, note);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-5 border-t border-white/5 pt-4">
      {!user ? (
        <Link
          to="/login"
          data-testid="verse-signin-to-reflect"
          className="text-sm text-[#A8A29E] hover:text-[#D4AF37] transition-colors duration-300"
        >
          Sign in to record what you received from these words →
        </Link>
      ) : (
        <>
          {savedNote && !open && (
            <div data-testid="verse-saved-note" className="mb-3">
              <p className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-1">What you received</p>
              <p className="text-[#F5F1E7] italic-serif text-lg leading-relaxed">{savedNote}</p>
            </div>
          )}
          {!open ? (
            <button
              data-testid={`reflect-toggle-${verse.reference}`}
              onClick={() => setOpen(true)}
              className="inline-flex items-center gap-2 text-sm text-[#A8A29E] hover:text-[#D4AF37] transition-colors duration-300"
            >
              <PenLine size={15} /> {savedNote ? "Edit your reflection" : "What did you receive from this?"}
            </button>
          ) : (
            <div>
              <textarea
                data-testid={`reflect-input-${verse.reference}`}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                placeholder="Write what these words stirred in you..."
                className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-[#F5F1E7] outline-none focus:border-[#D4AF37]/60 transition-colors duration-300 placeholder:text-[#A8A29E]/50 resize-none"
              />
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <button
                  data-testid={`reflect-save-${verse.reference}`}
                  onClick={save}
                  disabled={saving}
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#D4AF37] text-[#110D14] px-5 py-2 text-sm font-medium hover:bg-[#e6c860] transition-colors duration-300 disabled:opacity-50"
                >
                  <Check size={15} /> {saving ? "Keeping..." : "Keep"}
                </button>
                <button
                  onClick={() => setOpen(false)}
                  className="rounded-full border border-white/10 px-5 py-2 text-sm text-[#A8A29E] hover:text-[#F5F1E7] transition-colors duration-300"
                >
                  Cancel
                </button>
                <label className="flex items-center gap-2 text-sm text-[#A8A29E] cursor-pointer select-none ml-1">
                  <input
                    type="checkbox"
                    data-testid={`reflect-share-${verse.reference}`}
                    checked={shared}
                    onChange={(e) => setShared(e.target.checked)}
                    className="accent-[#D4AF37] w-4 h-4"
                  />
                  Share so others can be encouraged
                </label>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
